import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import { Settings as SettingsIcon, LogOut, KeyRound } from 'lucide-react';

const Settings = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage('');
    setPasswordError(false);
    if (newPassword !== confirmPassword) {
      setPasswordError(true);
      setPasswordMessage('New password and confirmation do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError(true);
      setPasswordMessage('New password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    try {
      const res = await authAPI.changePassword({
        currentPassword,
        newPassword,
      });
      if (res.data.success) {
        setPasswordMessage('Password updated successfully.');
        setPasswordError(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPasswordError(true);
        setPasswordMessage(res.data.error || 'Failed to update password.');
      }
    } catch (err) {
      setPasswordError(true);
      setPasswordMessage(err.response?.data?.error || err.message || 'Failed to update password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-full overflow-auto p-6">
      <div className="max-w-xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <SettingsIcon className="w-7 h-7 text-[#459EA3]" />
            Settings
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage your account and security</p>
        </div>

        <div className="space-y-8">
          {/* Change password */}
          <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-[#459EA3]" />
                Change password
              </h2>
            </div>
            <form onSubmit={handleChangePassword} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Current password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#459EA3] focus:border-[#459EA3]"
                  placeholder="Enter current password"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">New password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#459EA3] focus:border-[#459EA3]"
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm new password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#459EA3] focus:border-[#459EA3]"
                  placeholder="Confirm new password"
                  required
                />
              </div>
              {passwordMessage && (
                <p className={`text-sm ${passwordError ? 'text-red-600' : 'text-green-600'}`}>
                  {passwordMessage}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2.5 rounded-lg bg-[#459EA3] hover:bg-[#3d8a8e] text-white text-sm font-semibold disabled:opacity-50 transition-colors"
              >
                {loading ? 'Updating…' : 'Update password'}
              </button>
            </form>
          </section>

          {/* Logout */}
          <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <LogOut className="w-5 h-5 text-gray-500" />
                Sign out
              </h2>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-600 mb-4">Sign out of your account on this device.</p>
              <button
                type="button"
                onClick={handleLogout}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 border-red-200 text-red-700 hover:bg-red-50 text-sm font-semibold transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Log out
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Settings;
